import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowRight } from "@phosphor-icons/react";
import { lineUp } from "../lib/motion";
import { scrollToId } from "./Logo";
import { useLead } from "../context/LeadContext";

const LINES = ["Creative Marketing", "That Helps Your Business", "Get Seen, Trusted,", "and Chosen."];

export const Hero = () => {
  const { openLead } = useLead();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const blobY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section id="home" ref={ref} className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden bg-white pb-16 pt-32 md:pb-24">
      <motion.div style={{ y: blobY }} className="pointer-events-none absolute -right-24 -top-10 h-[520px] w-[520px] rounded-full bg-teal/25 blur-[120px] animate-blob-drift md:h-[680px] md:w-[680px]" />
      <motion.div style={{ y: blobY }} className="pointer-events-none absolute -left-40 top-1/3 h-[380px] w-[380px] rounded-full bg-beige/40 blur-[100px]" />

      <div className="pointer-events-none absolute inset-0 opacity-[0.5]" style={{ backgroundImage: "linear-gradient(#00000008 1px, transparent 1px), linear-gradient(90deg, #00000008 1px, transparent 1px)", backgroundSize: "72px 72px" }} />

      <div className="relative mx-auto w-full max-w-[1400px] px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="mb-8 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-teal"
        >
          <span className="h-px w-10 bg-teal" /> Creative Design &amp; Marketing
        </motion.div>

        <motion.h1 style={{ y: textY }} className="font-display font-black leading-[0.92] tracking-tight text-ink" data-testid="hero-headline">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span custom={i} variants={lineUp} initial="hidden" animate="show" className="block text-[10vw] leading-[0.92] md:text-[6.6vw] lg:text-[6vw]">
                {line}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <div className="mt-10 grid gap-8 md:grid-cols-[1fr_auto] md:items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
            className="max-w-xl font-sans text-base leading-relaxed text-zinc-600 md:text-lg"
          >
            Jakin Creative Design &amp; Marketing helps small businesses build stronger brands, better websites, social media content, and marketing systems that generate real growth.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7 }}
            className="flex flex-wrap gap-3"
          >
            <button onClick={() => openLead("consultation")} data-testid="hero-book-button" className="group flex items-center gap-3 bg-teal px-7 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-ink">
              Book a Consultation <ArrowRight weight="bold" className="transition-transform group-hover:translate-x-1" />
            </button>
            <button onClick={() => scrollToId("services")} data-testid="hero-services-button" className="group flex items-center gap-3 border border-ink px-7 py-4 text-sm font-bold uppercase tracking-wider text-ink transition-colors hover:bg-ink hover:text-white">
              View Our Services <ArrowDownRight weight="bold" className="transition-transform group-hover:translate-y-1" />
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
